import {makeAutoObservable, runInAction} from "mobx";
import {
  getImageEditorOptions,
  ImageEditorOptions
} from "../../components/molecules/imageEditor/imageEditorOptions";

// The different types of validation errors that can occur with the image editor
export type ImageEditorValidationState =
  | "validateFailed"
  | "isRequiredAndEmpty"
  | "isInvalidFileType"
  | "isAboveMaxFileSize"
  | "isAboveMaxPixels"
  | "isBelowMinWidth"
  | "isBelowMinHeight";

/*
 * The image editor has complex state management, which is best handled by an observable.
 * This is used in a similar way to the image observable, with the two being interchangeable in some cases.
 */
export class ImageEditorObservable {
  /**
   * The full uncropped image, to allow the user to edit their crop.
   */
  selectedImage: Blob | null = null;
  /**
   * The cropped version of the image.
   */
  croppedImage: Blob | null = null;

  fileName: string = "";
  fileId: string | null;
  hasNewUpload: boolean = false;

  validate?: (toValidate: Blob | null) => boolean;
  isDirty: boolean = false;

  isRequired: boolean;
  isImageEditorOpen: boolean = false;

  /**
   * Is the selected file below minimum resolution
   * @remarks This could be considered derived state, but as it's an async operation to calculate, we must cache the result.
   */
  isBelowMinWidth: boolean = false;
  isBelowMinHeight: boolean = false;
  isAboveMaxPixels: boolean = false;

  /**
   * Each browser has slightly different image support, and we don't know if an image is supported until the browser throws an error.
   */
  isInvalidFileType: boolean = false;

  options: ImageEditorOptions;

  constructor(
    fileId?: string,
    validate?: (toValidate: Blob | null) => boolean
  ) {
    this.fileId = fileId ?? null;
    this.validate = validate;
    this.isRequired = !!validate;

    this.options = getImageEditorOptions();

    makeAutoObservable(this);
  }

  get validationState(): ImageEditorValidationState | null {
    if (this.isDirty && this.validate && !this.validate(this.croppedImage)) {
      return "validateFailed";
    }

    if (this.isRequired && this.isDirty && !this.hasCroppedImage)
      return "isRequiredAndEmpty";

    if (!this.selectedImageIsImage || this.isInvalidFileType)
      return "isInvalidFileType";

    const croppedImageSize = this.croppedImage?.size ?? 0;
    const {maxFileSizeInBytes} = getImageEditorOptions(this.options);

    if (croppedImageSize > maxFileSizeInBytes) return "isAboveMaxFileSize";

    if (this.isAboveMaxPixels) return "isAboveMaxPixels";

    if (this.isBelowMinWidth) return "isBelowMinWidth";
    if (this.isBelowMinHeight) return "isBelowMinHeight";

    return null;
  }

  get file() {
    return this.croppedImage;
  }

  get isValid() {
    return this.validationState === null;
  }

  get hasCroppedImage() {
    return !!this.croppedImage;
  }

  get hasSelectedImage() {
    return !!this.selectedImage;
  }

  get selectedImageIsImage() {
    return this.selectedImage?.type.indexOf("image") !== -1;
  }

  clearAllImages = () => {
    this.setIsDirty();

    this.fileName = "";
    this.fileId = null;
    this.hasNewUpload = false;

    this.selectedImage = null;
    this.croppedImage = null;
    this.isBelowMinWidth = false;
    this.isBelowMinHeight = false;
    this.isAboveMaxPixels = false;
    this.isInvalidFileType = false;
  };

  setImageOptions = (options: ImageEditorOptions) => {
    this.options = getImageEditorOptions(options);
  };

  // This appears to have no calls, but is required in imagePreview.tsx
  resetFile = () => {
    this.clearAllImages();
  };

  setSelectedImage = (file: File) => {
    this.setIsDirty();

    this.selectedImage = file;
    this.isBelowMinWidth = false;
    this.isBelowMinHeight = false;
    this.isAboveMaxPixels = false;
    this.isInvalidFileType = false;

    const {minWidthInPixels, minHeightInPixels} = getImageEditorOptions(
      this.options
    );

    // Ensure that we are not below min dimensions, and open the editor if appropriate
    this.getImageDimensions(file).then((dimensions) => {
      if (dimensions) {
        const {width, height} = dimensions;
        runInAction(() => {
          this.isBelowMinWidth = width < minWidthInPixels;
          this.isBelowMinHeight = height < minHeightInPixels;
          if (
            !this.isBelowMinWidth &&
            !this.isBelowMinHeight &&
            this.selectedImageIsImage
          ) {
            this.openImageEditor();
          }
        });
      }
    });
  };

  openImageEditor = () => {
    this.isImageEditorOpen = true;
  };

  onSaveClicked = async (croppedImage: File) => {
    this.setIsDirty();
    this.croppedImage = croppedImage;
    this.fileId = "";
    this.hasNewUpload = true;
    this.fileName = croppedImage.name;
    this.isImageEditorOpen = false;

    const {maxSizeInPixels, minWidthInPixels, minHeightInPixels} =
      getImageEditorOptions(this.options);

    // Check if the resulting image is too large/small
    this.getImageDimensions(croppedImage).then((dimensions) => {
      if (dimensions) {
        const {width, height} = dimensions;
        runInAction(() => {
          this.isAboveMaxPixels = Math.max(width, height) > maxSizeInPixels;
          this.isBelowMinWidth = width < minWidthInPixels;
          this.isBelowMinHeight = height < minHeightInPixels;
        });
      }
    });
  };

  onCancelClicked = () => {
    if (this.croppedImage) {
      // Restore the existing file
      this.selectedImage = this.croppedImage;
    } else {
      this.clearAllImages();
    }
    this.isImageEditorOpen = false;
  };

  setIsDirty = (isDirty?: boolean) => {
    this.isDirty = isDirty ?? true;
  };

  async getImageDimensions(file: File) {
    try {
      const image = await createImageBitmap(file);
      return {
        width: image.width,
        height: image.height
      };
    } catch (ex) {
      if (ex instanceof DOMException) {
        runInAction(() => {
          this.isInvalidFileType = true;
        });
      }
      return undefined;
    }
  }

  setImageFromDownload(blob: Blob) {
    this.croppedImage = blob;
    this.selectedImage = blob;
    this.isBelowMinWidth = false;
    this.isBelowMinHeight = false;
    this.isAboveMaxPixels = false;
    this.isInvalidFileType = false;
    this.hasNewUpload = false;
    this.fileName = "";
    this.isDirty = false;
  }
}
